import {
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { EventShareService } from 'src/app/_services/event-share.service';
import { SidekickWidgetService } from 'src/app/_services/sidekick/sidekick-widget.service';
import { EmergencyEvacuationService } from 'src/app/_services/emergency/emergency-evacuation.service';
import { PropertyUiPb } from 'src/app/com/staybloom/uiproto/property/propertyUiProto';
import { WidgetInfo, WidgetSubmitResult } from 'src/app/_models/widget.models';
import { WIDGET_NAMES_CONSTANT } from 'src/app/constants/widgetNameConstant';

/**
 * Smart container / orchestrator for all floating widget types.
 *
 * Responsibilities (only these):
 *  - Drag wrapper and viewport positioning
 *  - Widget identity routing (generic form / SIDEKICK_SETUP / SOS_SIGN)
 *  - Close-on-outside-click and router-navigation-close behaviour
 *  - Emitting `closeModal` and `output` to the parent directive
 *
 * Business logic is fully delegated to:
 *  - SidekickWidgetService  — Sidekick connection lifecycle
 *  - EmergencyEvacuationService — Evacuation notification lifecycle
 *
 * UI rendering for each widget type is delegated to:
 *  - WidgetFormComponent    — generic form widgets
 *  - SidekickSetupComponent — SIDEKICK_SETUP widget
 *  - SosSignComponent       — SOS_SIGN widget
 *
 * Instantiated programmatically by RelativeClickPositionDirective.
 * Properties are set on the instance before change detection runs,
 * so ngOnInit() can safely read them.
 */
@Component({
  selector: 'app-update-widget',
  templateUrl: './update-widget.component.html',
  styleUrls: ['./update-widget.component.scss'],
  providers: [SidekickWidgetService, EmergencyEvacuationService],
})
export class UpdateWidgetComponent implements OnInit, OnDestroy {
  // Set directly by RelativeClickPositionDirective after createComponent()
  widgetInfo: WidgetInfo;

  propertyData: PropertyUiPb;

  /** True while a generic widget form save is in-flight */
  progress: boolean = false;

  @Output() closeModal = new EventEmitter<UpdateWidgetComponent>();
  @Output() output = new EventEmitter<WidgetSubmitResult>();

  private isClosed: boolean = true;
  private eventCounter: number = 1;
  private routerSubscription: Subscription;

  constructor(
    private elementRef: ElementRef,
    private router: Router,
    private eventShareService: EventShareService,
    private sidekickService: SidekickWidgetService,
  ) {}

  ngOnInit(): void {
    this.propertyData = JSON.parse(sessionStorage.getItem('propertyDetailsData') ?? 'null');
    this.eventShareService.setCurrentOpenWidget(this.widgetInfo?.widgetName);

    this.routerSubscription = this.router.events.subscribe(() => {
      this.close();
    });

    this.handler = () => this.close();
  }

  ngOnDestroy(): void {
    this.routerSubscription?.unsubscribe();
    if (this.isSidekickWidget) {
      this.sidekickService.destroy();
    }
  }

  get isGenericWidget(): boolean {
    return (
      this.widgetInfo?.widgetName !== 'SIDEKICK_SETUP' &&
      this.widgetInfo?.widgetName !== 'SOS_SIGN' &&
      this.widgetInfo?.widgetName !== WIDGET_NAMES_CONSTANT.GST_INFORMATION
    );
  }

  get isSidekickWidget(): boolean {
    return this.widgetInfo?.widgetName === 'SIDEKICK_SETUP';
  }

  get isSosWidget(): boolean {
    return this.widgetInfo?.widgetName === 'SOS_SIGN';
  }

  get isGstWidget(): boolean {
    return this.widgetInfo?.widgetName === WIDGET_NAMES_CONSTANT.GST_INFORMATION;
  }
  /** Called by RelativeClickPositionDirective to absolutely position the widget. */
  positionRelativeTo(x: number, y: number): void {
    this.elementRef.nativeElement.style.left = `${x}px`;
    this.elementRef.nativeElement.style.top = `${y}px`;
  }

  onFormSubmitted(data: any): void {
    if (this.isSidekickWidget) {
      // Service already set state to 'logging-in'; directive handles the API call
      this.output.emit({ response: true, data });
    } else {
      this.progress = true;
      this.output.emit({ response: true, data });
    }
  }

  onCancelled(): void {
    this.close();
  }

  onCloseStateChanged(isClosed: boolean): void {
    this.isClosed = isClosed;
    if (isClosed) {
      this.handler = () => {
        if (this.eventCounter > 1) {
          this.close();
        }
        this.eventCounter += 1;
      };
    } else {
      this.eventCounter = 1;
      this.handler = () => {};
    }
  }

  @HostListener('click')
  onSelfClick(): void {
    event?.stopPropagation();
  }

  @HostListener('document:click')
  handler(event?: any): void {}

  private close(): void {
    if (this.isClosed) {
      this.eventCounter = 1;
      this.resetFormFields();
      this.closeModal.emit(this);
    }
  }

  private resetFormFields(): void {
    this.widgetInfo?.form?.[0]?.fields?.forEach((field) => {
      field.value = '';
    });
  }

  onFetchGst(): void {
    this.output.emit({ response: true, data: 'fetchGst' });
  }

  onClearGst(): void {
    this.output.emit({ response: true, data: '' });
  }

  onRemoveGst(): void {
    this.output.emit({ response: true, data: 'removeGst' });
  }
}
